import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tabs, Button, Row, Col, Statistic, Space, Typography, Tag, Empty, message, Tooltip, Radio, Segmented, Popconfirm,
} from 'antd';
import {
  ReloadOutlined, DownloadOutlined, WarningOutlined, ExclamationCircleOutlined, FireOutlined, DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Text, Title } = Typography;

const BUCKETS = [
  { key: '1-7',     label: '1-7 dias',    countKey: '1-7' },
  { key: '8-30',    label: '8-30 dias',   countKey: '8-30' },
  { key: '31-60',   label: '31-60 dias',  countKey: '31-60' },
  { key: '61-90',   label: '61-90 dias',  countKey: '61-90' },
  { key: '91-180',  label: '91-180 dias', countKey: '91-180' },
  { key: '181-365', label: '181-365 dias', countKey: '181-365' },
  { key: '365plus', label: '+1 ano',      countKey: '365plus' },
  { key: 'never',   label: 'Nunca pagou', countKey: 'never_paid' },
  { key: 'all',     label: 'Todos',       countKey: 'total' },
];

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
};

const IrregularSubscriptions = () => {
  const [bucket, setBucket] = useState('all');
  const [usage, setUsage] = useState('recent');
  const [statusFilter, setStatusFilter] = useState(() => {
    const url = new URLSearchParams(window.location.search);
    const s = url.get('status');
    return s === 'PLANO_EXPIRADO' || s === 'ATIVO' ? s : 'ALL';
  });
  const [teams, setTeams] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [chargingId, setChargingId] = useState(null);

  const currentUser = readUser();
  const isAdmin = currentUser?.profile === 'ADMINISTRADOR';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/monitoring/irregular-subscriptions-v2', {
        params: { bucket, usage, status: statusFilter },
      });
      setTeams(res.data.teams || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      message.error('Erro ao carregar assinaturas irregulares');
    } finally {
      setLoading(false);
    }
  }, [bucket, usage, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const sendCharge = async (customer) => {
    setChargingId(customer.id);
    try {
      const res = await api.post(`/customers/${customer.id}/manual-charge`);
      message.success(`PIX enviado pro time ${res.data.team}`);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || 'Erro ao enviar PIX';
      if (status === 429) message.warning(msg);
      else message.error(msg);
    } finally {
      setChargingId(null);
    }
  };

  const exportCsv = () => {
    if (teams.length === 0) {
      message.info('Sem dados pra exportar');
      return;
    }
    const header = ['id', 'nome', 'status', 'whatsapp', 'chip', 'grupo_sync', 'valido_ate', 'dias_vencido', 'jogos_apos_vencimento', 'jogos_30d', 'ultimo_jogo', 'ultima_cobranca', 'ultima_cobranca_manual'];
    const lines = teams.map((t) => [
      t.id,
      `"${(t.game_name || '').replace(/"/g, '""')}"`,
      t.status || '',
      t.cellphone_number || '',
      t.instance_name || '',
      t.groupjid ? 'sim' : 'não',
      t.valid_until ? dayjs(t.valid_until).format('YYYY-MM-DD') : 'nunca-pagou',
      t.dias_vencido ?? '',
      t.games_apos_venc ?? 0,
      t.games_30d ?? 0,
      t.ultimo_jogo_at ? dayjs(t.ultimo_jogo_at).format('YYYY-MM-DD') : '',
      t.last_invoice_at ? dayjs(t.last_invoice_at).format('YYYY-MM-DD') : '',
      t.last_manual_charge_at ? dayjs(t.last_manual_charge_at).format('YYYY-MM-DD') : '',
    ].join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assinaturas-irregulares-${statusFilter}-${bucket}-${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const criticalityTag = (games_apos_venc, dias) => {
    if (games_apos_venc >= 20 || (dias && dias > 180)) return <Tag color="red" icon={<FireOutlined />}>Crítico</Tag>;
    if (games_apos_venc >= 5 || (dias && dias > 60)) return <Tag color="orange">Alto</Tag>;
    if (games_apos_venc > 0) return <Tag color="gold">Médio</Tag>;
    return <Tag color="default">Baixo</Tag>;
  };

  const statusTag = (s) => {
    if (s === 'ATIVO') return <Tag color="green">ATIVO</Tag>;
    if (s === 'PLANO_EXPIRADO') return <Tag color="red">EXPIRADO</Tag>;
    return <Tag>{s || '—'}</Tag>;
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'game_name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a href={`/customers/${r.id}/ficha`} target="_blank" rel="noreferrer">
            <Text strong>{v || '(sem nome)'}</Text>
          </a>
          {r.cellphone_number && (
            <Text type="secondary" style={{ fontSize: 11 }}>{r.cellphone_number}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      align: 'center',
      render: (v) => statusTag(v),
      filters: [
        { text: 'ATIVO', value: 'ATIVO' },
        { text: 'EXPIRADO', value: 'PLANO_EXPIRADO' },
      ],
      onFilter: (val, r) => r.status === val,
    },
    {
      title: 'Chip',
      dataIndex: 'instance_name',
      width: 110,
      render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Válida até',
      dataIndex: 'valid_until',
      width: 150,
      render: (v, r) => v ? (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM/YYYY')}</Text>
          <Text type="danger" style={{ fontSize: 11 }}>vencida há {r.dias_vencido}d</Text>
        </Space>
      ) : <Tag color="red">Nunca pagou</Tag>,
      sorter: (a, b) => (a.dias_vencido ?? -1) - (b.dias_vencido ?? -1),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Última cobrança',
      dataIndex: 'last_invoice_at',
      width: 140,
      render: (v, r) => v ? (
        <Space direction="vertical" size={0}>
          <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
            <Text style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
          </Tooltip>
          <Tag color={r.last_invoice_status === 'PAID' ? 'green' : r.last_invoice_status === 'PENDING' ? 'orange' : 'default'} style={{ fontSize: 10 }}>
            {r.last_invoice_status || '—'}
          </Tag>
        </Space>
      ) : <Text type="secondary">—</Text>,
      sorter: (a, b) => new Date(a.last_invoice_at || 0) - new Date(b.last_invoice_at || 0),
    },
    {
      title: (
        <Tooltip title="Jogos criados após o vencimento (uso indevido)">
          Jogos após venc. <ExclamationCircleOutlined />
        </Tooltip>
      ),
      dataIndex: 'games_apos_venc',
      width: 120,
      align: 'center',
      render: (v) => v > 0 ? (
        <Tag color={v >= 20 ? 'red' : v >= 5 ? 'orange' : 'gold'} style={{ fontWeight: 700 }}>{v}</Tag>
      ) : <Text type="secondary">0</Text>,
      sorter: (a, b) => (a.games_apos_venc || 0) - (b.games_apos_venc || 0),
    },
    {
      title: 'Jogos 30d',
      dataIndex: 'games_30d',
      width: 90,
      align: 'center',
      render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Último jogo',
      dataIndex: 'ultimo_jogo_at',
      width: 120,
      render: (v) => v ? (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
        </Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: (
        <Tooltip title="Última cobrança PIX manual enviada pelo admin (rate-limit 6h)">
          Cobrança manual <DollarOutlined />
        </Tooltip>
      ),
      dataIndex: 'last_manual_charge_at',
      width: 130,
      render: (v) => {
        if (!v) return <Text type="secondary">—</Text>;
        const d = dayjs(v);
        const withinRate = dayjs().diff(d, 'hour') < 6;
        return (
          <Tooltip title={d.format('DD/MM/YYYY HH:mm')}>
            <Tag color={withinRate ? 'orange' : 'default'} style={{ fontSize: 11 }}>
              {d.fromNow()}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => new Date(a.last_manual_charge_at || 0) - new Date(b.last_manual_charge_at || 0),
    },
    {
      title: 'Criticidade',
      key: 'crit',
      width: 100,
      render: (_, r) => criticalityTag(r.games_apos_venc || 0, r.dias_vencido),
    },
    ...(isAdmin ? [{
      title: 'Ação',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, r) => {
        const lastManual = r.last_manual_charge_at ? dayjs(r.last_manual_charge_at) : null;
        const withinRate = !lastManual || dayjs().diff(lastManual, 'hour') >= 6;
        const hasRecentGames = (r.games_30d || 0) > 0;
        const canSend = withinRate && hasRecentGames;
        const tooltipMsg = !hasRecentGames
          ? 'Sem jogos nos últimos 30d — time morto, não vale enviar'
          : !withinRate
            ? `Aguarde ${6 - dayjs().diff(lastManual, 'hour')}h (última: ${lastManual.fromNow()})`
            : (lastManual ? `Última manual: ${lastManual.fromNow()}` : 'Envia PIX de cobrança no DM do organizador');
        return (
          <Popconfirm
            title="Enviar cobrança PIX?"
            description={`Vai gerar um PIX de R$29,90 e enviar msg pra ${r.game_name}`}
            okText="Enviar"
            cancelText="Cancelar"
            onConfirm={() => sendCharge(r)}
            disabled={!canSend || chargingId === r.id}
          >
            <Tooltip title={tooltipMsg}>
              <Button
                size="small"
                type="primary"
                icon={<DollarOutlined />}
                loading={chargingId === r.id}
                disabled={!canSend}
              >
                PIX
              </Button>
            </Tooltip>
          </Popconfirm>
        );
      },
    }] : []),
  ];

  const totalGamesLost = counts.total_games_criados || 0;
  const totalIrregular = counts.total || 0;

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <WarningOutlined style={{ color: '#faad14' }} /> Assinaturas irregulares
          </Title>
          <Text type="secondary">
            Times com assinatura vencida (ATIVOS escaparam do cron • EXPIRADOS já foram suspensos)
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Atualizar
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportCsv}
              disabled={teams.length === 0}
            >
              Exportar CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Total no filtro"
              value={totalIrregular}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Usando o sistema"
              value={counts.total_com_jogos || 0}
              valueStyle={{ color: '#cf1322' }}
              suffix={totalIrregular > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({Math.round(100 * (counts.total_com_jogos || 0) / totalIrregular)}%)
                </Text>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Jogos após vencimento"
              value={totalGamesLost}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Ativos / Expirados</Text>
              <Space>
                <Tag color="green">ATIVO: {counts.total_ativo || 0}</Tag>
                <Tag color="red">EXP: {counts.total_expirado || 0}</Tag>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size="middle">
          <Space size={4}>
            <Text type="secondary">Status:</Text>
            <Segmented
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: `Todos (${(Number(counts.total_ativo) || 0) + (Number(counts.total_expirado) || 0)})`, value: 'ALL' },
                { label: `Ativos (${counts.total_ativo || 0})`, value: 'ATIVO' },
                { label: `Expirados (${counts.total_expirado || 0})`, value: 'PLANO_EXPIRADO' },
              ]}
            />
          </Space>
          <Space size={4}>
            <Text type="secondary">Uso:</Text>
            <Radio.Group value={usage} onChange={(e) => setUsage(e.target.value)} size="small">
              <Radio.Button value="recent">Usando 30d</Radio.Button>
              <Radio.Button value="historical">Já usou após vencer</Radio.Button>
              <Radio.Button value="any">Todos</Radio.Button>
            </Radio.Group>
          </Space>
        </Space>
      </Card>

      <Tabs
        activeKey={bucket}
        onChange={setBucket}
        items={BUCKETS.map((b) => ({
          key: b.key,
          label: (
            <span>
              {b.label}{' '}
              <Tag color={b.key === bucket ? 'red' : 'default'} style={{ marginLeft: 4 }}>
                {counts[b.countKey] ?? 0}
              </Tag>
            </span>
          ),
        }))}
      />

      {teams.length === 0 && !loading ? (
        <Card>
          <Empty description="Nenhum time irregular neste filtro" />
        </Card>
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={teams}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} times` }}
        />
      )}
    </div>
  );
};

export default IrregularSubscriptions;
